/**
 * ── HOW A BLOB IS DECODED — the ONE derivation, and it is PURE so the CATALOG can refuse on it ───
 *
 * P1.5·A1 made the BLOB the unit of transfer and gave it ONE decode: a blob records the
 * `decodeKey` of the asset that decoded it, and a second asset wanting the same bytes decoded
 * DIFFERENTLY is `failed('unsupported')` naming both ids (`service.acquireBlob`). That refusal is
 * correct and it is also the LAST possible moment to make it — at that point the bytes have
 * shipped, the catalog has passed every check, and WHICH of the two assets loses depends on
 * acquire order and on what happened to be resident, so the same catalog and the same code can
 * produce different pictures on different runs.
 *
 * A decode is not a function of bytes alone: a PNG's colour space comes from the asset's LABELS
 * (`axonRig`'s measured facts — `base_color` is LINEAR, `rgb_mask` is sRGB), and its role comes
 * from `slot`/`labels` too. All of that is CATALOG DATA, so the key is derivable at publish time —
 * which is where `LibraryCatalogSchema`'s blob refine now derives it, turning an order-dependent
 * runtime refusal into a publish-time one naming both asset ids before a byte ships.
 *
 * ⚠️ THERE IS EXACTLY ONE DERIVATION AND THE SERVICE CALLS IT. `service.decoderFor` returns
 * `decodeKeyFor(...)` as its key rather than composing its own string; a second spelling here
 * would let the tool and the runtime disagree about which catalogs are legal, which is worse than
 * either answer alone.
 *
 * PURE: no three, no DOM (`tests/purity.test.ts`). The colour space is a TOKEN (`srgb` | `linear`)
 * and `loaders/texture.ts` maps it onto `THREE.SRGBColorSpace` / `THREE.NoColorSpace` — one
 * definition of WHICH colour space, two spellings of how to say it.
 */

/** The colour roles a catalog may name (slot or label). `albedo` is the one sRGB member. */
export const TEXTURE_COLOR_ROLES: readonly string[] = ['albedo', 'normal', 'orm', 'mask', 'emissive', 'image'];

/** The cook's label spellings (`tools/library/compose.ts`) — one place, every reader. */
export const MAP_LABEL_PREFIX = 'map-';
export const COLOURSPACE_LABEL_PREFIX = 'colourspace-';

/** The two colour spaces this runtime decodes into, as data. */
export type ColorSpaceToken = 'srgb' | 'linear';

/** What a catalog entry has to say about a texture's decode — every field is catalog data. */
export interface TextureDecodeInput {
  slot: string | null;
  labels: readonly string[];
}

export function colorSpaceTokenForRole(role: string): ColorSpaceToken {
  return role === 'albedo' ? 'srgb' : 'linear';
}

/** `slot` wins, then a `map-<use>` label, then a bare known-role label, else `image` (linear). */
export function textureRoleOf(asset: TextureDecodeInput): string {
  if (asset.slot !== null && asset.slot !== '') return asset.slot;
  const mapLabel = asset.labels.find((l) => l.startsWith(MAP_LABEL_PREFIX) && l.length > MAP_LABEL_PREFIX.length);
  if (mapLabel !== undefined) return mapLabel.slice(MAP_LABEL_PREFIX.length);
  return asset.labels.find((l) => TEXTURE_COLOR_ROLES.includes(l)) ?? 'image';
}

/** A `colourspace-srgb` / `colourspace-linear` label is authoritative; otherwise the role decides. */
export function textureColorSpaceTokenOf(asset: TextureDecodeInput): ColorSpaceToken {
  if (asset.labels.includes(`${COLOURSPACE_LABEL_PREFIX}srgb`)) return 'srgb';
  if (asset.labels.includes(`${COLOURSPACE_LABEL_PREFIX}linear`)) return 'linear';
  return colorSpaceTokenForRole(textureRoleOf(asset));
}

/** The shape of a catalog entry this derivation reads — `LibraryAsset` is assignable. */
export interface DecodeAssetInput extends TextureDecodeInput {
  kind: string;
}

/**
 * What ONE (asset, role, file) wants its blob decoded AS. Two callers agreeing on this string
 * SHARE the decode; two disagreeing over one sha256 is a catalog that cannot be served — the
 * schema refuses it, and `acquireBlob` keeps the same test as belt-and-braces for a SECONDARY
 * catalog merged at runtime, which no publish ever saw.
 */
export function decodeKeyFor(asset: DecodeAssetInput, role: string, file: { format: string }): string {
  if (role === 'rig') return 'json';
  if (asset.kind === 'clip') return 'clips';
  if (asset.kind === 'texture') {
    return `texture:${file.format}:${textureRoleOf(asset)}:${textureColorSpaceTokenOf(asset)}`;
  }
  return 'gltf';
}
